// app/src/simulations/txVerify.ts
import type { Connection, Finality } from "@solana/web3.js";

type Commitment = "processed" | "confirmed" | "finalized";

function toFinality(c?: Commitment): Finality {
  // web3.js Finality does not accept "processed" in some method signatures
  return c === "finalized" ? "finalized" : "confirmed";
}

export async function waitForSignature(
  connection: Connection,
  signature: string,
  opts: { commitment?: Commitment; timeoutMs: number; pollMs: number }
) {
  const start = Date.now();

  while (Date.now() - start < opts.timeoutMs) {
    const st = await connection.getSignatureStatus(signature, {
      searchTransactionHistory: true,
    });

    // st.value is SignatureStatus | null (not SignatureResult)
    const v: any = st?.value;
    const confirmationStatus = v?.confirmationStatus ?? null;

    if (confirmationStatus === "confirmed" || confirmationStatus === "finalized") {
      return { confirmationStatus, err: v?.err ?? null };
    }

    await new Promise((r) => setTimeout(r, opts.pollMs));
  }

  return { confirmationStatus: null as string | null, err: { timeout: true, finality: toFinality(opts.commitment) } };
}

export async function fetchTxDetails(
  connection: Connection,
  signature: string,
  commitment?: Commitment
) {
  const finality = toFinality(commitment);

  const tx = await connection.getTransaction(signature, {
    commitment: finality,
    maxSupportedTransactionVersion: 0,
  });

  const feeLamports = tx?.meta?.fee ?? null;
  return { feeLamports };
}
